import { useId, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { RadioGroup, RadioGroupItem } from '@/components/atoms/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Checkbox } from '@/components/atoms/checkbox';
import { Textarea } from '@/components/atoms/textarea';
import { useAcquisitionChannels } from '@/hooks/channels/useAcquisitionChannels';
import { useCreateAcquisitionChannel } from '@/hooks/channels/useCreateAcquisitionChannel';
import { useCompanyBrandColor } from '@/hooks/forms-engine/useCompanyBrandColor';
import { useSetFormTemplateLeadSource } from '@/hooks/forms-engine/useFormTemplates';
import { isApiError } from '@/lib/api/errors';
import { cn } from '@/lib/utils';
import {
  headerClashesWithBackground,
  PUBLIC_FORM_BACKGROUNDS,
  PUBLIC_FORM_HEADER_STYLES,
  PUBLIC_FORM_QUESTION_STYLES,
  headerEndFor,
  publicFormPageBackground,
} from '@/lib/forms/public-form-theme';
import {
  BUSINESS_DETAIL_OPTIONS,
  normalizeHexColor,
  PUBLIC_FORM_DEFAULTS,
  PUBLIC_FORM_FONTS,
  PUBLIC_SETTINGS_LIMITS,
  redirectUrlIssue,
  type AfterSubmitMode,
  type PublicSettingsDraft,
} from '@/lib/forms/public-settings';
import type {
  PublicFormFont,
  PublicFormHeaderStyle,
  PublicFormQuestionStyle,
} from '@/types/formsEngine';

/**
 * "Public page" settings of an INTAKE form: branding, which company details
 * the header shows, a consent checkbox, the submit label, and what happens
 * after submitting.
 */
export function IntakePublicPageSettings({
  value,
  onChange,
  templateId,
  acquisitionChannelId,
}: {
  value: PublicSettingsDraft;
  onChange: (next: PublicSettingsDraft) => void;
  /**
   * The saved template's id. Absent while creating a new template (nothing
   * to `PATCH .../lead-source` against yet) — the Lead source picker below
   * only renders once this is set.
   */
  templateId?: string;
  /** The template's current channel, as last read from the server. */
  acquisitionChannelId?: string | null;
}) {
  const id = useId();
  const update = (patch: Partial<PublicSettingsDraft>) => onChange({ ...value, ...patch });
  const redirectIssue = value.redirectUrl.trim() ? redirectUrlIssue(value) : null;

  return (
    <section className="space-y-4 border-t pt-5" aria-labelledby={`${id}-heading`}>
      <div>
        <p id={`${id}-heading`} className="text-xs font-semibold">
          Public page
        </p>
        <p className="text-[10px] text-muted-foreground">
          What customers see on the hosted link and the embedded form
        </p>
      </div>

      <BrandingFields value={value} update={update} />

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium">Show on form</legend>
        <p className="text-[10px] text-muted-foreground">
          Company details in the page header. Anything unticked is never sent to the page.
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {BUSINESS_DETAIL_OPTIONS.map(({ key, label }) => (
            <label key={key} className="flex cursor-pointer items-center gap-2 text-xs">
              <Checkbox
                checked={value.businessDetails[key]}
                onCheckedChange={(checked) =>
                  update({ businessDetails: { ...value.businessDetails, [key]: checked === true } })
                }
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {templateId ? (
        <LeadSourcePicker templateId={templateId} acquisitionChannelId={acquisitionChannelId} />
      ) : null}

      <LimitedField
        id={`${id}-consent`}
        label="Consent / terms checkbox"
        value={value.consentText}
        limit={PUBLIC_SETTINGS_LIMITS.consentText}
        hint="Customers must tick this box to submit. Leave empty for no checkbox."
      >
        <Textarea
          id={`${id}-consent`}
          value={value.consentText}
          maxLength={PUBLIC_SETTINGS_LIMITS.consentText}
          rows={3}
          placeholder="I agree to be contacted about my request."
          onChange={(event) => update({ consentText: event.target.value })}
        />
      </LimitedField>

      <LimitedField
        id={`${id}-submit`}
        label="Submit button label"
        value={value.submitLabel}
        limit={PUBLIC_SETTINGS_LIMITS.submitLabel}
      >
        <Input
          id={`${id}-submit`}
          value={value.submitLabel}
          maxLength={PUBLIC_SETTINGS_LIMITS.submitLabel}
          placeholder={PUBLIC_FORM_DEFAULTS.submitLabel}
          onChange={(event) => update({ submitLabel: event.target.value })}
        />
      </LimitedField>

      <div className="space-y-2">
        <Label className="text-xs" id={`${id}-after`}>
          After submit
        </Label>
        <RadioGroup
          aria-labelledby={`${id}-after`}
          value={value.afterSubmit}
          onValueChange={(afterSubmit) => update({ afterSubmit: afterSubmit as AfterSubmitMode })}
          className="gap-2"
        >
          <label className="flex cursor-pointer items-center gap-2 text-xs">
            <RadioGroupItem value="MESSAGE" />
            Show a message
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs">
            <RadioGroupItem value="REDIRECT" />
            Redirect to a page
          </label>
        </RadioGroup>
      </div>

      {value.afterSubmit === 'MESSAGE' ? (
        <>
          <LimitedField
            id={`${id}-title`}
            label="Success title"
            value={value.successTitle}
            limit={PUBLIC_SETTINGS_LIMITS.successTitle}
          >
            <Input
              id={`${id}-title`}
              value={value.successTitle}
              maxLength={PUBLIC_SETTINGS_LIMITS.successTitle}
              placeholder={PUBLIC_FORM_DEFAULTS.successTitle}
              onChange={(event) => update({ successTitle: event.target.value })}
            />
          </LimitedField>
          <LimitedField
            id={`${id}-message`}
            label="Success message"
            value={value.successMessage}
            limit={PUBLIC_SETTINGS_LIMITS.successMessage}
          >
            <Textarea
              id={`${id}-message`}
              value={value.successMessage}
              maxLength={PUBLIC_SETTINGS_LIMITS.successMessage}
              rows={3}
              placeholder={PUBLIC_FORM_DEFAULTS.successMessage}
              onChange={(event) => update({ successMessage: event.target.value })}
            />
          </LimitedField>
        </>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-redirect`} className="text-xs">
            Redirect URL
          </Label>
          <Input
            id={`${id}-redirect`}
            type="url"
            inputMode="url"
            maxLength={PUBLIC_SETTINGS_LIMITS.redirectUrl}
            value={value.redirectUrl}
            placeholder="https://example.com/thanks"
            aria-invalid={redirectIssue ? true : undefined}
            aria-describedby={`${id}-redirect-hint`}
            onChange={(event) => update({ redirectUrl: event.target.value })}
          />
          <p
            id={`${id}-redirect-hint`}
            className={
              redirectIssue ? 'text-[10px] text-destructive' : 'text-[10px] text-muted-foreground'
            }
          >
            {redirectIssue ??
              'Customers go here after submitting. If an embedded form cannot leave its frame, they see a thank-you message with a Continue link.'}
          </p>
        </div>
      )}
    </section>
  );
}

/** Shown in the colour picker when neither the form nor the company has a colour. */
const PICKER_PLACEHOLDER = '#2563eb';

/**
 * Brand colour and font. The colour is only ever stored as strict hex (the
 * hex box commits a value once it is complete); null means "use the company
 * colour" from invoice branding.
 */
function BrandingFields({
  value,
  update,
}: {
  value: PublicSettingsDraft;
  update: (patch: Partial<PublicSettingsDraft>) => void;
}) {
  const id = useId();
  const companyColor = useCompanyBrandColor(value.primaryColor === null);
  // What is typed in the hex box, kept apart from the draft so a half-typed
  // colour neither saves nor gets wiped while the owner is typing.
  const [hexText, setHexText] = useState(value.primaryColor ?? '');
  const [syncedColor, setSyncedColor] = useState(value.primaryColor);
  if (syncedColor !== value.primaryColor) {
    setSyncedColor(value.primaryColor);
    setHexText(value.primaryColor ?? '');
  }
  const hexIssue =
    hexText.trim() && !normalizeHexColor(hexText) ? 'Use a hex colour like #1a2b3c.' : null;

  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-medium">Branding</legend>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-hex`} className="text-xs">
          Brand colour
        </Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label="Brand colour picker"
            value={value.primaryColor ?? companyColor ?? PICKER_PLACEHOLDER}
            onChange={(event) => update({ primaryColor: normalizeHexColor(event.target.value) })}
            className="h-9 w-10 shrink-0 cursor-pointer rounded-md border bg-background p-1"
          />
          <Input
            id={`${id}-hex`}
            aria-label="Brand colour hex"
            value={hexText}
            maxLength={7}
            placeholder="Company colour"
            spellCheck={false}
            aria-invalid={hexIssue ? true : undefined}
            aria-describedby={`${id}-hex-hint`}
            className="h-9 font-mono text-xs"
            onChange={(event) => {
              const text = event.target.value;
              setHexText(text);
              // Emptied: back to the company colour.
              if (!text.trim()) {
                update({ primaryColor: null });
                return;
              }
              const color = normalizeHexColor(text);
              if (color) update({ primaryColor: color });
            }}
            // Leaving the box shows what will actually be saved: a half-typed
            // colour gives way to the stored one (empty for the company colour).
            onBlur={() => setHexText(value.primaryColor ?? '')}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={value.primaryColor === null}
            onClick={() => update({ primaryColor: null })}
          >
            Use company colour
          </Button>
        </div>
        <p
          id={`${id}-hex-hint`}
          className={
            hexIssue ? 'text-[10px] text-destructive' : 'text-[10px] text-muted-foreground'
          }
        >
          {hexIssue ??
            (value.primaryColor
              ? 'The header, buttons and picked answers use this colour.'
              : 'Using your company colour from invoice branding.')}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-font`} className="text-xs">
          Font
        </Label>
        <Select
          value={value.font}
          onValueChange={(font) => update({ font: font as PublicFormFont })}
        >
          <SelectTrigger id={`${id}-font`} className="h-9" aria-label="Font">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PUBLIC_FORM_FONTS.map((font) => (
              <SelectItem key={font.value} value={font.value}>
                <span style={{ fontFamily: font.family }}>{font.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <ThemeFields value={value} update={update} brandColor={value.primaryColor ?? companyColor} />
    </fieldset>
  );
}

/** Stands in for the brand colour in the swatches when neither form nor company has one. */
const DEFAULT_BRAND_SWATCH = '#0a0b0d';

/**
 * Header style, page background and question style. Each choice shows a
 * small picture of itself in the colour that will actually be used.
 */
function ThemeFields({
  value,
  update,
  brandColor,
}: {
  value: PublicSettingsDraft;
  update: (patch: Partial<PublicSettingsDraft>) => void;
  brandColor: string | null;
}) {
  const id = useId();
  const brand = brandColor ?? DEFAULT_BRAND_SWATCH;
  const custom =
    value.backgroundColor !== null &&
    !PUBLIC_FORM_BACKGROUNDS.some((option) => option.value === value.backgroundColor);
  // A minimal header is white, so only a coloured banner can vanish into the page.
  const clash =
    value.headerStyle !== 'minimal' && headerClashesWithBackground(brand, value.backgroundColor);
  // The swatches are drawn in the brand colour directly, not through
  // `--primary`, which stays the dashboard's own (light or dark) token here.
  const swatchFill: Record<PublicFormHeaderStyle, string> = {
    gradient: `linear-gradient(135deg, ${brand} 0%, ${headerEndFor(brand)} 100%)`,
    solid: brand,
    minimal: brand,
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label id={`${id}-header`} className="text-xs">
          Header style
        </Label>
        <RadioGroup
          aria-labelledby={`${id}-header`}
          value={value.headerStyle}
          onValueChange={(headerStyle) =>
            update({ headerStyle: headerStyle as PublicFormHeaderStyle })
          }
          className="grid grid-cols-3 gap-2"
        >
          {PUBLIC_FORM_HEADER_STYLES.map((option) => (
            <label
              key={option.value}
              title={option.description}
              className={cn(
                'flex cursor-pointer flex-col gap-1.5 rounded-lg border p-1.5 text-[10px] transition-colors hover:bg-muted/40',
                value.headerStyle === option.value &&
                  'border-foreground/60 ring-1 ring-foreground/20',
              )}
            >
              <span aria-hidden className="block overflow-hidden rounded border bg-white">
                <span
                  className={cn('block', option.value === 'minimal' ? 'h-1' : 'h-5')}
                  style={{ background: swatchFill[option.value] }}
                />
                <span className="block space-y-0.5 p-1">
                  <span className="block h-1 w-3/4 rounded-full bg-muted" />
                  <span className="block h-1 w-1/2 rounded-full bg-muted" />
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <RadioGroupItem value={option.value} className="size-3" />
                {option.label}
              </span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-1.5">
        <Label id={`${id}-background`} className="text-xs">
          Page background
        </Label>
        <div role="group" aria-labelledby={`${id}-background`} className="flex flex-wrap gap-2">
          {PUBLIC_FORM_BACKGROUNDS.map((option) => {
            const selected = value.backgroundColor === option.value;
            return (
              <button
                key={option.label}
                type="button"
                aria-label={option.label}
                aria-pressed={selected}
                title={option.label}
                onClick={() => update({ backgroundColor: option.value })}
                className={cn(
                  'size-8 rounded-full border shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected && 'ring-2 ring-foreground/70 ring-offset-2',
                )}
                style={{ background: publicFormPageBackground(brand, option.value) }}
              />
            );
          })}
          <label
            title="Custom colour"
            className={cn(
              'relative flex size-8 cursor-pointer items-center justify-center overflow-hidden rounded-full border shadow-sm focus-within:ring-2 focus-within:ring-ring',
              custom && 'ring-2 ring-foreground/70 ring-offset-2',
            )}
            style={{
              background: custom
                ? (value.backgroundColor ?? undefined)
                : 'conic-gradient(#f87171, #facc15, #4ade80, #60a5fa, #c084fc, #f87171)',
            }}
          >
            <input
              type="color"
              aria-label="Custom background colour"
              value={value.backgroundColor ?? '#ffffff'}
              onChange={(event) =>
                update({ backgroundColor: normalizeHexColor(event.target.value) })
              }
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </label>
        </div>
        <p className={clash ? 'text-[10px] text-destructive' : 'text-[10px] text-muted-foreground'}>
          {clash
            ? 'The header will barely stand out from this background. Pick a lighter background or another brand colour.'
            : 'Shown behind the form on the shared link. Embedded forms stay white until you pick a colour.'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label id={`${id}-questions`} className="text-xs">
          Question style
        </Label>
        <RadioGroup
          aria-labelledby={`${id}-questions`}
          value={value.questionStyle}
          onValueChange={(questionStyle) =>
            update({ questionStyle: questionStyle as PublicFormQuestionStyle })
          }
          className="grid grid-cols-2 gap-2"
        >
          {PUBLIC_FORM_QUESTION_STYLES.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-lg border p-2 text-xs transition-colors hover:bg-muted/40',
                value.questionStyle === option.value &&
                  'border-foreground/60 ring-1 ring-foreground/20',
              )}
            >
              <RadioGroupItem value={option.value} className="mt-0.5" />
              <span>
                <span className="block font-medium">{option.label}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {option.description}
                </span>
              </span>
            </label>
          ))}
        </RadioGroup>
      </div>
    </div>
  );
}

function LimitedField({
  id,
  label,
  value,
  limit,
  hint,
  children,
}: {
  id: string;
  label: string;
  value: string;
  limit: number;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id} className="text-xs">
          {label}
        </Label>
        <span className="text-[10px] tabular-nums text-muted-foreground">
          {value.length}/{limit}
        </span>
      </div>
      {children}
      {hint ? <p className="text-[10px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

const NEW_CHANNEL = '__new_channel__';
const NOT_SET = '__not_set__';

function messageFrom(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message || fallback;
  return fallback;
}

/**
 * Which acquisition channel this intake form's future submissions report
 * under (`PATCH /forms/templates/:id/lead-source`). Every published intake
 * form already has one — `ensureChannelForTemplate` creates "Web form:
 * <name>" the first time it publishes — but the owner can point it at a
 * different (active) channel here, or spin up a brand new one inline.
 */
function LeadSourcePicker({
  templateId,
  acquisitionChannelId,
}: {
  templateId: string;
  acquisitionChannelId?: string | null;
}) {
  const id = useId();
  const channelsQuery = useAcquisitionChannels();
  const setLeadSource = useSetFormTemplateLeadSource();
  const createChannel = useCreateAcquisitionChannel();
  const [creating, setCreating] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const channels = channelsQuery.data ?? [];
  const currentChannel = acquisitionChannelId
    ? channels.find((channel) => channel.id === acquisitionChannelId)
    : undefined;
  // The current channel can go archived after it was set here — the picker
  // only ever *offers* active channels, but it must still show what is
  // actually selected, archived or not.
  const isArchivedChannel = Boolean(currentChannel && !currentChannel.isActive);
  const selectableChannels = [
    ...(isArchivedChannel && currentChannel ? [currentChannel] : []),
    ...channels.filter((channel) => channel.isActive),
  ];

  function handleValueChange(next: string) {
    if (next === NEW_CHANNEL) {
      setCreating(true);
      setError(null);
      return;
    }
    if (next === NOT_SET || next === acquisitionChannelId) return;
    setLeadSource.mutate({ templateId, acquisitionChannelId: next });
  }

  async function submitNewChannel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = newChannelName.trim();
    if (!trimmed) return;
    try {
      const created = await createChannel.mutateAsync({ name: trimmed });
      setCreating(false);
      setNewChannelName('');
      setError(null);
      setLeadSource.mutate({ templateId, acquisitionChannelId: created.id });
    } catch (caught) {
      setError(messageFrom(caught, 'That channel could not be created.'));
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${id}-lead-source`} className="text-xs">
        Lead source
      </Label>
      <p className="text-[10px] text-muted-foreground">
        The acquisition channel new leads from this form report under.
      </p>
      <Select value={acquisitionChannelId ?? NOT_SET} onValueChange={handleValueChange}>
        <SelectTrigger id={`${id}-lead-source`} className="h-9" aria-label="Lead source">
          <SelectValue placeholder="Choose a channel" />
        </SelectTrigger>
        <SelectContent>
          {!acquisitionChannelId ? (
            <SelectItem value={NOT_SET} disabled>
              Not set yet
            </SelectItem>
          ) : null}
          {selectableChannels.map((channel) => (
            <SelectItem key={channel.id} value={channel.id}>
              {channel.name}
              {!channel.isActive ? ' (archived)' : ''}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={NEW_CHANNEL}>New channel…</SelectItem>
        </SelectContent>
      </Select>

      {isArchivedChannel ? (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-[10px]">
            This channel is archived — new leads report as Website. Choose another.
          </AlertDescription>
        </Alert>
      ) : null}

      {creating ? (
        <form
          className="flex flex-wrap items-end gap-2 rounded-md border border-dashed p-2.5"
          onSubmit={(event) => void submitNewChannel(event)}
        >
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor={`${id}-new-channel-name`} className="text-[10px]">
              New channel name
            </Label>
            <Input
              id={`${id}-new-channel-name`}
              value={newChannelName}
              onChange={(event) => setNewChannelName(event.target.value)}
              placeholder="Radio ad"
              className="h-8 text-xs"
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={!newChannelName.trim() || createChannel.isPending}
          >
            {createChannel.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Create
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setCreating(false);
              setNewChannelName('');
              setError(null);
            }}
          >
            Cancel
          </Button>
        </form>
      ) : null}

      {error ? <p className="text-[10px] text-destructive">{error}</p> : null}
    </div>
  );
}
